import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { BarChartData } from "../../../../types/recharts/barChart";
import { Legend } from "../../../Charts/Legend/Legend";
import { LegendProps } from "../../../Charts/Legend/Legend.type";
import { StackedHorizontalBarChart } from "../../../Charts/StackedHorizontalBarChart/StackedHorizontalBarChart";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

import { COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS } from "./CollectionOfWEEEByEUMemberStates.const";
import { CollectionOfWEEEByEUMemberStatesData, CollectionOfWEEEByEUMemberStatesProps } from "./CollectionOfWEEEByEUMemberStates.type";

const CollectionOfWEEEByEUMemberStates: FC<CollectionOfWEEEByEUMemberStatesProps> = ({ data }) => {
	const chartSize = useBreakpoint();

	const [legendDirection, setLegendDirection] = useState<LegendProps["direction"]>("horizontal");

	const translatedBarChartData = useMemo<BarChartData>(() => {
		return data.map((dataEntry) => ({
			...dataEntry,
			name: dataEntry.name,
		}));
	}, [data]);

	const legendData = useMemo(() => {
		return COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS.map((dataKey) => {
			const label = typeof dataKey?.translation === "string" ? dataKey.translation : dataKey.name;

			return {
				color: dataKey.color,
				label: label,
			};
		});
	}, []);

	useEffect(() => {
		switch (chartSize) {
			case "xs":
			case "sm":
				setLegendDirection("vertical");
				break;

			case "md":
			case "lg":
			case "xl":
			default:
				setLegendDirection("horizontal");
				break;
		}
	}, [chartSize]);

	const endOfBarLabelValueAccessor = useCallback((entry: CollectionOfWEEEByEUMemberStatesData): string => {
		const total =
			entry?.large +
				entry?.small +
				entry?.other || 0;
		return `${new Intl.NumberFormat("nl-BE").format(total)} kg`;
	}, []);

	return (
		<div className="flex flex-col gap-4">

			<StackedHorizontalBarChart
				YAxisLabel="Collected Waste Electrical and Electronic Equipment"
				yAxisLabelClassName="text-lg text-gray-800 font-bold"
				data={translatedBarChartData}
				dataKeys={COLLECTION_OF_WEEE_BY_EU_MEMBER_STATES_CHART_DATA_KEYS}
				YAxisProps={{
					dataKey: "name",
					padding: { top: 10, bottom: 10 },
					fontSize: 14,
				}}
				XAxisProps={{ unit: " kg", tickCount: 5, tick: { fontSize: 12 } }}
				showBarLabel={false}
				tooltipUnit=" kg"
				endOfBarLabelValueAccessor={endOfBarLabelValueAccessor}
				endOfBarLabelPosition="left-of-axis"
				chartSubtitle="WEEE, in kg per inhabitant"
			/>

			<div className="self-stretch md:self-center max-w-4xl">
				<Legend items={legendData} direction={legendDirection} />
			</div>

		</div>
	);
};

export { CollectionOfWEEEByEUMemberStates };